import { z } from 'zod';
import { wallClock, zonedToInstant } from './business-time.js';
import { idSchema, instantSchema } from './common.js';
/**
 * Notes and tasks (RELAY-945) attach to any record: `entityType` + `entityId`, the same
 * polymorphic pair documents use. A new kind joins by adding itself here and to the API's
 * record registry, not by new tables. Not the custom-field entity list: a record kind can have
 * a timeline, notes and tasks before it has custom fields (RELAY-949's client records).
 */
export const RECORD_ENTITIES = [
    'candidates',
    'companies',
    'contacts',
    'projects',
    'deals',
    'jobs',
];
export const recordEntitySchema = z.enum(RECORD_ENTITIES);
/** Plural names for a record-type filter (Team Tasks). */
export const RECORD_ENTITY_LABELS = {
    candidates: 'Candidates',
    companies: 'Companies',
    contacts: 'Contacts',
    projects: 'Projects',
    deals: 'Deals',
    jobs: 'Jobs',
};
const personSchema = z.object({ id: idSchema, name: z.string(), email: z.string() });
/**
 * A note's body. **No length cap** (the acceptance criterion): the schema has no `.max()`, the
 * column is `text`, and the note routes raise the request body limit.
 */
export const noteBodySchema = z.string().trim().min(1, 'A note needs some text');
/**
 * How a note's body is written, and so how it is shown and sent to RecruitCRM. `markdown`: the
 * notes from before the rich text editor, Markdown that may hold raw HTML.
 * `html`: the editor's HTML, sanitised by the API against `NOTE_HTML_TAGS` before it is stored.
 * A write that names no format is `markdown`, so an old browser tab or an API key never has its
 * Markdown read as HTML.
 */
export const NOTE_BODY_FORMATS = ['markdown', 'html'];
export const noteBodyFormatSchema = z.enum(NOTE_BODY_FORMATS);
/** The tags an `html` note keeps; every other tag is dropped and its text kept. */
export const NOTE_HTML_TAGS = [
    'p',
    'br',
    'b',
    'strong',
    'i',
    'em',
    'u',
    's',
    'ul',
    'ol',
    'li',
    'a',
    'span',
];
/** The only CSS an `html` note keeps, on `span`: a text colour and a highlight. */
export const NOTE_HTML_STYLES = ['color', 'background-color'];
/** A colour value an `html` note keeps: hex, rgb()/rgba(), or a name, as the editor and RecruitCRM write one. */
export const NOTE_HTML_COLOURS = [
    /^#[0-9a-f]{3,8}$/i,
    /^rgba?\(\s*[\d.\s,%]+\)$/i,
    /^[a-z]{3,20}$/i,
];
/** The link targets an `html` note keeps; any other `href` is dropped on write. */
export const NOTE_HTML_HREF = /^\s*(https?:|mailto:|tel:)/i;
export const noteInputSchema = z.object({
    entityType: recordEntitySchema,
    entityId: idSchema,
    body: noteBodySchema,
    bodyFormat: noteBodyFormatSchema.default('markdown'),
    pinned: z.boolean().default(false),
});
/** A new `body` without `bodyFormat` is `markdown`; `bodyFormat` needs a `body` with it. */
export const notePatchSchema = z
    .object({ body: noteBodySchema, bodyFormat: noteBodyFormatSchema, pinned: z.boolean() })
    .partial()
    .refine((p) => p.body !== undefined || p.pinned !== undefined, 'Nothing to change')
    .refine((p) => p.bodyFormat === undefined || p.body !== undefined, {
    message: 'bodyFormat needs a body',
    path: ['bodyFormat'],
});
/** The pipeline trigger that wrote a task or note, which then has no person as its author. */
export const createdByAutomationSchema = z.object({
    triggerId: idSchema,
    /** Null once the trigger is deleted. */
    name: z.string().nullable(),
});
export const noteSchema = z.object({
    id: idSchema,
    entityType: recordEntitySchema,
    entityId: idSchema,
    body: z.string(),
    bodyFormat: noteBodyFormatSchema,
    pinned: z.boolean(),
    author: personSchema.nullable(),
    createdByAutomation: createdByAutomationSchema.nullable().optional(),
    createdAt: instantSchema,
    updatedAt: instantSchema,
    /** Set when the body was edited after creation. */
    editedAt: instantSchema.nullable(),
});
/**
 * A record whose notes a Notes card lists (`GET /notes/sources`): the record itself and, for a
 * project, its company, contacts, deals, jobs and shortlisted candidates the caller can see.
 * `canWrite`: the caller may add a note there.
 */
export const noteSourceSchema = z.object({
    entityType: recordEntitySchema,
    entityId: idSchema,
    label: z.string(),
    canWrite: z.boolean(),
});
/**
 * A task's status (TS-1739; `open` before it, migrated to `todo`). `done` is the completed
 * state: it sets `completedAt`, and the checkbox toggles between it and `todo`.
 */
export const TASK_STATUSES = ['todo', 'in_progress', 'done'];
export const taskStatusSchema = z.enum(TASK_STATUSES);
export const TASK_STATUS_LABELS = {
    todo: 'To do',
    in_progress: 'In progress',
    done: 'Done',
};
/** The lists overlap, so "automated" is a task flag, not a type. */
export const MANUAL_TASK_TYPES = [
    'follow_up',
    'reminder',
    'pre_work',
    'report',
    'update_crm_project',
    'send_email',
    'send_message',
    'phone_call',
    'meeting',
    'health_check',
    'upsell',
    'crossell',
];
export const AUTOMATED_TASK_TYPES = [
    'send_email',
    'send_message',
    'send_whatsapp',
    'generate_proposal',
    'send_proposal',
    'check_payment',
];
export const TASK_TYPES = [
    ...MANUAL_TASK_TYPES,
    'send_whatsapp',
    'generate_proposal',
    'send_proposal',
    'check_payment',
];
export const taskTypeSchema = z.enum(TASK_TYPES);
/** The types a person may set through the API. */
export const manualTaskTypeSchema = z.enum(MANUAL_TASK_TYPES);
export const TASK_TYPE_LABELS = {
    follow_up: 'Follow-up',
    reminder: 'Reminder',
    pre_work: 'Pre-work',
    report: 'Report',
    update_crm_project: 'Update CRM Project',
    send_email: 'Send Email',
    send_message: 'Send Message',
    phone_call: 'Phone call',
    meeting: 'Meeting',
    health_check: 'Health Check',
    upsell: 'Upsell',
    crossell: 'Crossell',
    send_whatsapp: 'Send WhatsApp',
    generate_proposal: 'Generate Proposal',
    send_proposal: 'Send Proposal',
    check_payment: 'Check Payment',
};
/** My Tasks' filter: a status, `open` (anything not done — the default) or `all`. */
export const MY_TASKS_FILTERS = ['open', ...TASK_STATUSES, 'all'];
export const myTasksFilterSchema = z.enum(MY_TASKS_FILTERS);
/**
 * When a task is due, relative to now (Team Tasks, TS-1743): before now, before next Monday in
 * New York (the team's week runs Monday–Sunday, Eastern), after that, or no due date. The API
 * filters on these windows and the page groups by them, through the same `weekBounds`.
 */
export const DUE_WINDOWS = ['overdue', 'this_week', 'later', 'none'];
export const dueWindowSchema = z.enum(DUE_WINDOWS);
export const DUE_WINDOW_LABELS = {
    overdue: 'Overdue',
    this_week: 'This week',
    later: 'Later',
    none: 'No due date',
};
/** Now, and the start of next week (Monday 00:00 New York) — the edges of the due windows. */
export function weekBounds(now = new Date()) {
    const { weekday, asUtc } = wallClock(now);
    const today = asUtc - (asUtc % 86_400_000);
    return { now, nextWeek: new Date(zonedToInstant(today + (7 - weekday) * 86_400_000)) };
}
export function dueWindow(dueAt, bounds = weekBounds()) {
    if (!dueAt)
        return 'none';
    const due = Date.parse(dueAt);
    if (due < bounds.now.getTime())
        return 'overdue';
    return due < bounds.nextWeek.getTime() ? 'this_week' : 'later';
}
/** `?automated=false` lists manual tasks only, `true` automated only; absent, both. */
export const automatedFilterSchema = z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true'));
/** Comma-separated ids in a query string, like the timeline's `types=`. */
const commaSeparatedIds = (max) => z
    .string()
    .optional()
    .transform((s) => s
    ? s
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean)
    : undefined)
    .pipe(z.array(idSchema).min(1).max(max).optional());
/** `GET /tasks/team?teamIds=t&assigneeIds=a,b&status=open&due=this_week&entityType=candidates`. */
export const teamTasksQuerySchema = z.object({
    /** Narrows to these people; anyone outside the caller's `task.read` scope is ignored. */
    assigneeIds: commaSeparatedIds(200),
    /**
     * Narrows to the members of any of these teams (TS-1861), resolved on the server, so a team's
     * size never reaches the URL. With `assigneeIds` too, both apply.
     */
    teamIds: commaSeparatedIds(50),
    /** My Tasks' filter: `open` (not done, the default), a status, or `all`. */
    status: myTasksFilterSchema.default('open'),
    due: dueWindowSchema.optional(),
    entityType: recordEntitySchema.optional(),
    type: taskTypeSchema.optional(),
    automated: automatedFilterSchema,
});
/**
 * `GET /tasks/mine/type-counts` and `/tasks/team/type-counts`: how many tasks of each type the
 * list's other filters leave, for the type filter. `type` null counts the untyped ones.
 */
export const taskTypeCountsSchema = z.array(z.object({ type: taskTypeSchema.nullable(), count: z.number().int().nonnegative() }));
/**
 * A task's description (TS-1740) is Markdown, rendered like a note body. Unlike a note it is
 * capped: it is a brief for the task, not a document, and the audit row keeps it whole. A
 * blank one is stored as null.
 */
export const TASK_DESCRIPTION_MAX = 5000;
const taskDescriptionSchema = z.string().trim().max(TASK_DESCRIPTION_MAX).nullable();
export const taskInputSchema = z.object({
    entityType: recordEntitySchema,
    entityId: idSchema,
    title: z.string().trim().min(1).max(300),
    description: taskDescriptionSchema.optional(),
    type: manualTaskTypeSchema.nullable().optional(),
    dueAt: instantSchema.nullable().optional(),
    /** Defaults to the creator. Must be a member of the organisation. */
    assigneeId: idSchema.optional(),
});
export const taskPatchSchema = z
    .object({
    title: z.string().trim().min(1).max(300),
    description: taskDescriptionSchema,
    type: manualTaskTypeSchema.nullable(),
    dueAt: instantSchema.nullable(),
    assigneeId: idSchema,
    status: taskStatusSchema,
})
    .partial();
/** Where an automated task's run stands, derived from its `trigger_runs` row. */
export const TASK_AUTOMATION_STATES = [
    'scheduled',
    'retrying',
    'sent',
    'skipped',
    'failed',
];
export const TASK_AUTOMATION_STATE_LABELS = {
    scheduled: 'Scheduled',
    retrying: 'Retrying',
    sent: 'Sent',
    skipped: 'Skipped',
    failed: 'Failed',
};
export const taskAutomationSchema = z.object({
    state: z.enum(TASK_AUTOMATION_STATES),
    attempts: z.number().int(),
    responseStatus: z.number().int().nullable(),
    /** The last error, or why the run was skipped. */
    error: z.string().nullable(),
    processedAt: instantSchema.nullable(),
});
export const taskSchema = z.object({
    id: idSchema,
    entityType: recordEntitySchema,
    entityId: idSchema,
    /** The record's display name (e.g. the candidate's name), for lists like My Tasks. */
    entityLabel: z.string(),
    /** The project a deal or job lives under, where the client opens it (TS-1738); else null. */
    entityProjectId: idSchema.nullable(),
    title: z.string(),
    description: z.string().nullable(),
    type: taskTypeSchema.nullable(),
    /** Created by the system (a pipeline trigger) for an action it runs at `dueAt`; read-only. */
    automated: z.boolean(),
    /** An automated task's run; null for a manual task (or a run not found). */
    automation: taskAutomationSchema.nullable().optional(),
    dueAt: instantSchema.nullable(),
    status: taskStatusSchema,
    assignee: personSchema.nullable(),
    createdBy: personSchema.nullable(),
    createdByAutomation: createdByAutomationSchema.nullable().optional(),
    completedAt: instantSchema.nullable(),
    createdAt: instantSchema,
    updatedAt: instantSchema,
    /** Files attached to the task (TS-1741), uploaded and not removed; listed by GET /tasks/:id/attachments. */
    attachmentCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=notes-tasks.js.map